import { useQuery } from '@tanstack/react-query';
import { getHouseTasksHousesHouseIdTasksGetOptions } from '@/client/@tanstack/react-query.gen.ts';
import { ApiTask } from '@/types/tasks.ts';

const HOUSE_TASKS_POLLING_INTERVAL = 1000; // 3 seconds
const HOUSE_TASKS_STALE_TIME = 2000; // 2 seconds

type HouseApiTaskOptions = {
	/**
	 * Whether to enable continuous polling (only when there are active local tasks)
	 */
	shouldPoll?: boolean;

	/**
	 * Whether to immediately fetch tasks on mount
	 * Useful for initial load without polling
	 */
	immediate?: boolean;
};
/**
 * Hook to fetch tasks for a specific house using the new bulk endpoint
 * This replaces individual task polling with a single request per house
 */
export const useHouseApiTasks = (houseId?: number | null, options?: HouseApiTaskOptions) => {
	return useQuery({
		...getHouseTasksHousesHouseIdTasksGetOptions({
			path: { house_id: houseId! },
		}),
		// Only enable when we have a house and we actually need to poll
		enabled: !!houseId && (options?.immediate || (!options?.immediate && options?.shouldPoll)),
		refetchInterval: options?.shouldPoll ? HOUSE_TASKS_POLLING_INTERVAL : false,
		refetchOnWindowFocus: false,
		staleTime: HOUSE_TASKS_STALE_TIME,
		select: (data): ApiTask[] => {
			// Transform the loosely typed response to our ApiTask type
			return (data as unknown as ApiTask[]).filter((task) => {
				// Filter out tasks older than 24 hours
				const taskCreatedAt = parseInt(task.metadata.created_at) * 1000;
				const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
				return taskCreatedAt > twentyFourHoursAgo;
			});
		},
	});
};
